import React, { useEffect, useState } from 'react';
import { kpiAPI } from '../services/api';
import { toast } from 'react-toastify';
import InfoTooltip from '../components/InfoTooltip';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function KpiCyclesAdmin() {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name:'', periodStart:'', periodEnd:'', submitDeadline:'', reviewDeadline:'', status:'open' });
  const [editingCycle, setEditingCycle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try { const { data } = await kpiAPI.listCycles(); setCycles(data); }
    catch(e){ setError(e.response?.data?.error || e.message); }
    finally{ setLoading(false); }
  };

  useEffect(()=>{ load(); },[]);

  const create = async () => {
    // simple validation
    if (!form.name?.trim()) return toast.error('Please enter a cycle name');
    if (!form.periodStart || !form.periodEnd) return toast.error('Please select period start and end');
    if (!form.submitDeadline || !form.reviewDeadline) return toast.error('Please select both deadlines');
    try {
      await kpiAPI.createCycle(form);
      setForm({ name:'', periodStart:'', periodEnd:'', submitDeadline:'', reviewDeadline:'', status:'open' });
      await load();
      toast.success('Cycle created');
    } catch(e){ 
      setError(e.response?.data?.error || e.message); 
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const edit = async () => {
    // simple validation
    if (!form.name?.trim()) return toast.error('Please enter a cycle name');
    if (!form.periodStart || !form.periodEnd) return toast.error('Please select period start and end');
    if (!form.submitDeadline || !form.reviewDeadline) return toast.error('Please select both deadlines');
    try {
      await kpiAPI.updateCycle(editingCycle.id, form);
      setForm({ name:'', periodStart:'', periodEnd:'', submitDeadline:'', reviewDeadline:'', status:'open' });
      setEditingCycle(null);
      setIsEditing(false);
      await load();
      toast.success('Cycle updated');
    } catch(e){ 
      setError(e.response?.data?.error || e.message); 
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const handleEdit = (cycle) => {
    setEditingCycle(cycle);
    setIsEditing(true);
    setForm({
      name: cycle.name,
      periodStart: cycle.periodStart,
      periodEnd: cycle.periodEnd,
      submitDeadline: cycle.submitDeadline ? new Date(cycle.submitDeadline).toISOString().slice(0, 16) : '',
      reviewDeadline: cycle.reviewDeadline ? new Date(cycle.reviewDeadline).toISOString().slice(0, 16) : '',
      status: cycle.status
    });
  };

  const handleCancelEdit = () => {
    setEditingCycle(null);
    setIsEditing(false);
    setForm({ name:'', periodStart:'', periodEnd:'', submitDeadline:'', reviewDeadline:'', status:'open' });
  };

  const handleDelete = async (cycle) => {
    if (!window.confirm(`Are you sure you want to delete the cycle "${cycle.name}"? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(cycle.id);
    try {
      await kpiAPI.deleteCycle(cycle.id);
      await load();
      toast.success('Cycle deleted');
    } catch(e){ 
      setError(e.response?.data?.error || e.message); 
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">KPI Cycles</h1>
      <p className="text-sm text-gray-600 mb-4">Create a monthly cycle. Employees submit KPIs by the submit deadline; managers review by the review deadline.</p>
      {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
      <div className="bg-white border rounded p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Cycle Name</label>
            <input className="border rounded px-2 py-2 w-full" placeholder="Oct 2025" value={form.name} onChange={e=>setForm({ ...form, name:e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Period Start <InfoTooltip text="First day of the KPI period (inclusive)" /></label>
            <input className="border rounded px-2 py-2 w-full" type="date" value={form.periodStart} onChange={e=>setForm({ ...form, periodStart:e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Period End <InfoTooltip text="Last day of the KPI period (inclusive)" /></label>
            <input className="border rounded px-2 py-2 w-full" type="date" value={form.periodEnd} onChange={e=>setForm({ ...form, periodEnd:e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Submit Deadline <InfoTooltip text="Employees should submit self-assessments by this date/time" /></label>
            <input className="border rounded px-2 py-2 w-full" type="datetime-local" value={form.submitDeadline} onChange={e=>setForm({ ...form, submitDeadline:e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Review Deadline <InfoTooltip text="Managers should complete reviews by this date/time" /></label>
            <input className="border rounded px-2 py-2 w-full" type="datetime-local" value={form.reviewDeadline} onChange={e=>setForm({ ...form, reviewDeadline:e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Initial Status <InfoTooltip text="Use open during the month; close/lock after reviews" /></label>
            <select className="border rounded px-2 py-2 w-full" value={form.status} onChange={e=>setForm({ ...form, status:e.target.value })}>
            <option value="open">open</option>
            <option value="draft">draft</option>
            <option value="closed">closed</option>
            <option value="locked">locked</option>
            </select>
            <p className="text-[11px] text-gray-500 mt-1">Use open during the month; close/lock after reviews.</p>
          </div>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          {isEditing ? (
            <>
              <button className="px-4 py-2 bg-green-600 text-white rounded w-full sm:w-auto" onClick={edit}>
                Update Cycle
              </button>
              <button className="px-4 py-2 bg-gray-500 text-white rounded w-full sm:w-auto" onClick={handleCancelEdit}>
                Cancel
              </button>
            </>
          ) : (
            <button className="px-4 py-2 bg-indigo-600 text-white rounded w-full sm:w-auto" onClick={create}>
              Create Cycle
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border rounded overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submit Deadline</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review Deadline</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cycles.map(c => (
              <tr key={c.id} className={editingCycle?.id === c.id ? 'bg-blue-50' : ''}>
                <td className="px-4 py-2">
                  <div className="flex items-center">
                    <span className="font-medium">{c.name}</span>
                    {editingCycle?.id === c.id && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Editing</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2">{c.periodStart} → {c.periodEnd}</td>
                <td className="px-4 py-2">{c.submitDeadline ? new Date(c.submitDeadline).toLocaleString() : '-'}</td>
                <td className="px-4 py-2">{c.reviewDeadline ? new Date(c.reviewDeadline).toLocaleString() : '-'}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    c.status === 'open' ? 'bg-green-100 text-green-800' :
                    c.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    c.status === 'closed' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(c)}
                      disabled={isEditing && editingCycle?.id !== c.id}
                      className="inline-flex items-center p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Edit cycle"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(c)}
                      disabled={deleting === c.id || isEditing}
                      className="inline-flex items-center p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete cycle"
                    >
                      {deleting === c.id ? (
                        <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


